import { useForm } from "@inertiajs/react";
import { Check, Edit3, Lock, Tag, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { CategorySubcategorySelector } from "@/components/category-subcategory-selector";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Category, Subcategory } from "@/types";

interface InlineEditableCategorySubcategoryProps {
  categoryId: number | null;
  subcategoryId: number | null;
  category?: Category | null;
  subcategory?: Subcategory | null;
  updateUrl: string;
  disabled?: boolean;
}

export default function InlineEditableCategorySubcategory({
  categoryId,
  subcategoryId,
  category,
  subcategory,
  updateUrl,
  disabled = false,
}: InlineEditableCategorySubcategoryProps) {
  const [isEditing, setIsEditing] = useState(false);

  const { data, setData, patch, processing, errors, reset } = useForm({
    category_id: categoryId,
    subcategory_id: subcategoryId,
  });

  const startEditing = () => {
    if (!disabled) {
      reset();
      setIsEditing(true);
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);
    reset();
  };

  const saveChanges = () => {
    patch(updateUrl, {
      preserveScroll: true,
      onSuccess: () => {
        setIsEditing(false);
        toast.success("Categorización actualizada");
      },
    });
  };

  if (isEditing) {
    return (
      <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
        <CategorySubcategorySelector
          categoryId={data.category_id}
          subcategoryId={data.subcategory_id}
          onCategoryChange={(id) => setData("category_id", id)}
          onSubcategoryChange={(id) => setData("subcategory_id", id)}
          categoryError={errors.category_id}
          subcategoryError={errors.subcategory_id}
          disabled={processing}
        />
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={cancelEditing}
            disabled={processing}
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button size="sm" onClick={saveChanges} disabled={processing}>
            <Check className="h-4 w-4 mr-2" />
            Guardar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative group">
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Tag className="h-4 w-4" />
          Categoría
        </div>
        <p className="font-medium">{category?.name ?? "No asignada"}</p>
      </div>
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Tag className="h-4 w-4" />
          Subcategoría
        </div>
        <p className="font-medium">{subcategory?.name ?? "No asignada"}</p>
      </div>

      <div className="absolute top-0 right-0">
        {disabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex h-8 w-8 items-center justify-center text-muted-foreground">
                <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            </TooltipTrigger>
            <TooltipContent>Este campo no se puede editar</TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={startEditing}
                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Editar categorización</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
